import { Rate, TopRatesFilter } from "../states/dashboard-state";
import { StatisticsFilter } from "../../../../../redux/models/statistics/statistics-filter";
import * as moment from 'moment';
import {
    BookingOfficesResponse, ReadOfficeIdsRequest, ReadOfficeIdsResult, ReadStatistcsRequest,
    ReadStatistcsResponse, ReadStatistcsResult, ReadTopRatesRequest, ReadTopRatesResponse, ReadTopRatesResult,
    TopRate
} from "./dashboard.model";
import { DestinationType } from "../../../../../redux/models/statistics/destination-type";
import { Destination } from "../../../../../redux/models/statistics/destination";
import { ICompanyNameAndOffice } from "../../models/dashboard.model";


export const FI_STATUS_ACT = 'ACT';
export const DATE_FORMAT = 'YYYY-MM-DD';

export const createReadStatistcsRequest = (filter: StatisticsFilter, includeKPIs: boolean): ReadStatistcsRequest => {
    return {
        bookingStartDate: formatDate(filter.startDate),
        bookingEndDate: formatDate(filter.endDate),
        cityCode: getCityCode(filter.destination),
        countryCode: getCountryCode(filter.destination),
        fiStatus: FI_STATUS_ACT,
        officeIds: filter.officeIds,
        includeKPIs
    };
};

export const createStatisticsUrlParams = (filter: StatisticsFilter, includeKPIs: boolean): string => {
    const params = new URLSearchParams();
    filter.officeIds.forEach(officeId => params.append('officeIds', officeId));
    params.append('startDate', formatDate(filter.startDate));
    params.append('endDate', formatDate(filter.endDate));
    params.append('includeKPIs', includeKPIs.toString());
    if (filter.destination?.type === DestinationType.CITY && filter.destination.code) {
        params.append('cityCode', filter.destination.code);
    }
    else if (filter.destination?.type === DestinationType.COUNTRY && filter.destination.code) {
        params.append('countryCode', filter.destination.code);
    }
    return params.toString();
}

export const createReadStatisticsResponse = (result: ReadStatistcsResult): ReadStatistcsResponse => {

    if (result.error) {
        throw new Error(result.error.message);
    }

    return {
        kpis: result.kpis,
        monthlyStatistics: result.monthlyStatistics
    };
};

export const toStatisticsResponse = (result: ReadStatistcsResult): ReadStatistcsResponse => {
    return {
        kpis: result.kpis,
        monthlyStatistics: result.monthlyStatistics
    }
}

export const createReadTopRatesRequest = (filter: StatisticsFilter): ReadTopRatesRequest => {
    return {
        bookingStartDate: formatDate(filter.startDate),
        bookingEndDate: formatDate(filter.endDate),
        cityCode: getCityCode(filter.destination),
        countryCode: getCountryCode(filter.destination),
        fiStatus: FI_STATUS_ACT,
        officeIds: filter.officeIds,
        standardRates: true,
        limit: 10
    };
};

export const createTopRatesUrlParams = (filter: StatisticsFilter): string => {
    const params = new URLSearchParams();
    filter.officeIds.forEach(officeId => params.append('officeIds', officeId));
    params.append('startDate', formatDate(filter.startDate));
    params.append('endDate', formatDate(filter.endDate));
    params.append('limit', '10');
    params.append('standardRates', 'true');
    if (filter.destination?.type === DestinationType.CITY && filter.destination.code) {
        params.append('cityCode', filter.destination.code);
    }
    if (filter.destination?.type === DestinationType.COUNTRY && filter.destination.code) {
        params.append('countryCode', filter.destination.code);
    }
    return params.toString();
}

export const createReadTopRatesResponse = (result: ReadTopRatesResult): ReadTopRatesResponse => {

    if (result.error) {
        throw new Error(result.error.message);
    }

    return {
        topRates: result.rates
    }
};

export const toTopRatesResponse = (result: TopRate[]): ReadTopRatesResponse => {
    return { topRates: result };
}

export const createReadRatesResponse = (result: ReadTopRatesResult): Rate[] => {

    if (result.error) {
        throw new Error(result.error.message);
    }

    return result.rates[0].topRateList;
};

export const toRates = (result: TopRate[]): Rate[] => {
    return result[0].topRateList;
}


export const getCityCode = (destination: Destination): string => {
    if (!destination || destination?.type !== DestinationType.CITY) {
        return null;
    }
    return destination.code;
};

export const getCountryCode = (destination: Destination): string => {
    if (!destination || destination?.type !== DestinationType.COUNTRY) {
        return null;
    }
    return destination.code;
};

export const createReadShortTopRatesRequest = (filter: StatisticsFilter, topRatesFilter: TopRatesFilter,
    city: string, country: string): ReadTopRatesRequest => {

    return {
        bookingStartDate: formatDate(filter.startDate),
        bookingEndDate: formatDate(filter.endDate),
        cityCode: city,
        countryCode: country,
        fiStatus: FI_STATUS_ACT,
        officeIds: filter.officeIds,
        destinationType: topRatesFilter.destinationType,
        statisticType: topRatesFilter.statisticType,
        standardRates: false,
        limit: 10
    };
};

export const createShortTopRatesUrlParams = (filter: StatisticsFilter, topRatesFilter: TopRatesFilter,
    city: string, country: string): string => {
    const params = new URLSearchParams();
    filter.officeIds.forEach(officeId => params.append('officeIds', officeId));
    params.append('startDate', formatDate(filter.startDate));
    params.append('endDate', formatDate(filter.endDate));
    params.append('limit', '10');
    params.append('standardRates', 'false');
    params.append('destinationType', topRatesFilter.destinationType);
    params.append('statisticsType', topRatesFilter.statisticType);
    if (city) {
        params.append('cityCode', city);
    }
    if (country) {
        params.append('countryCode', country);
    }
    return params.toString();
}

export const createReadOfficeIdsRequest = (currentOffice: string): ReadOfficeIdsRequest => {
    return {
        officeId: currentOffice
    };
};

export const createReadOfficeIdsResponse = (result: ICompanyNameAndOffice[] | ReadOfficeIdsResult): BookingOfficesResponse => {
    if (isReadOfficeIdRequest(result)) {
        return { bookingOffices: result.bookingOfficeIds }
    }
    return {
        bookingOffices: result
    };
};

export const toBookingOfficesResponse = (response: string[]): BookingOfficesResponse => {
    return {
        bookingOffices: response
    }
}

export const createOfficeIdsUrlParams = (currentOffice: string): string => {
    const params = new URLSearchParams();
    params.append('officeId', currentOffice);
    return params.toString();
}

export const formatDate = (date: string): string => {
    return moment(date).format(DATE_FORMAT);
}

function isReadOfficeIdRequest(result: ICompanyNameAndOffice[] | ReadOfficeIdsResult): result is ReadOfficeIdsResult {
    return (result as ReadOfficeIdsResult).bookingOfficeIds !== undefined
} 